import {AxiosError} from 'axios';

export enum ErrorType {
  NETWORK = 'NETWORK',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  PERMISSION = 'PERMISSION',
  NOT_FOUND = 'NOT_FOUND',
  SERVER = 'SERVER',
  UNKNOWN = 'UNKNOWN',
  API_ERROR = 'API_ERROR'
}

export interface ErrorDetails {
  field?: string;
  value?: unknown;
  constraints?: Record<string, string>;
  errors?: Array<{
    stat_code: number;
    stat_message: string;
  }>;
  error?: string;
  status?: number;
  message?: string;
  details?: unknown;
  providerId?: number; // Add providerId for provider-specific errors
}

interface ApiErrorResponse {
  stat_code?: number;
  stat_message?: string;
  errors?: Array<{
    stat_code: number;
    stat_message: string;
  }>;
  error?: string;
  status?: number;
  message?: string;
}

export class AppError extends Error {
  constructor (
    message: string,
    public readonly type: ErrorType = ErrorType.UNKNOWN,
    public readonly details?: ErrorDetails,
    public readonly code?: number,
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'AppError';
  }

  static fromApiError (error: AxiosError<ApiErrorResponse> | Error | unknown): AppError {
    // Handle UserAPIDataSource errors (Error with isApiError flag and response property)
    if (error instanceof Error && (error as any).isApiError) {
      const apiError = error as any;
      const response = apiError.response;

      if (response?.stat_message) {
        return new AppError(
          response.stat_message,
          this.getErrorTypeFromCode(response.stat_code || apiError.status || 400),
          response as ErrorDetails,
          response.stat_code || apiError.status,
          error
        );
      }

      // If no stat_message, use the error message (which is already set from UserAPIDataSource)
      return new AppError(
        apiError.message || 'حدث خطأ غير متوقع',
        this.getErrorTypeFromCode(apiError.status || 400),
        response as ErrorDetails,
        apiError.status,
        error
      );
    }

    if (this.isAxiosError(error)) {
      const response = error.response?.data;

      if (response?.stat_message) {
        // Extract stat_message and clean it - remove any JSON concatenated at the end
        let message = typeof response.stat_message === 'string'
          ? response.stat_message.trim()
          : String(response.stat_message || 'حدث خطأ غير متوقع');

        // Remove JSON object if concatenated at the end
        // Pattern: "text{...JSON...}" should become just "text"
        const jsonIndex = message.indexOf('{');
        if (jsonIndex > 0) {
          // Check if the part after { looks like JSON (contains stat_code, stat_message, data, or error)
          const afterJson = message.substring(jsonIndex);
          if (afterJson.includes('stat_code') ||
              afterJson.includes('stat_message') ||
              afterJson.includes('"data"') ||
              afterJson.includes('"error"') ||
              afterJson.includes('\'data\'') ||
              afterJson.includes('\'error\'')) {
            // Remove everything from { onwards
            message = message.substring(0, jsonIndex).trim();
          }
        }

        return new AppError(
          message,
          this.getErrorTypeFromCode(response.stat_code || error.response?.status || 400),
          response as ErrorDetails,
          response.stat_code || error.response?.status,
          error
        );
      }

      if (response?.errors?.length) {
        const apiError = response.errors[0];
        return new AppError(
          apiError.stat_message,
          this.getErrorTypeFromCode(apiError.stat_code),
          response as ErrorDetails,
          apiError.stat_code,
          error
        );
      }

      if (response?.error) {
        return new AppError(
          response.error,
          ErrorType.SERVER,
          response as ErrorDetails,
          error.response?.status,
          error
        );
      }

      if (response?.message) {
        return new AppError(
          response.message,
          this.getErrorTypeFromCode(error.response?.status || 400),
          response as ErrorDetails,
          error.response?.status,
          error
        );
      }
    }

    if (error instanceof Error && error.message?.includes('Network Error')) {
      return new AppError(
        'فشل الاتصال بالخادم. يرجى التحقق من اتصالك بالإنترنت',
        ErrorType.NETWORK,
        undefined,
        0,
        error
      );
    }

    return new AppError(
      'حدث خطأ غير متوقع',
      ErrorType.UNKNOWN,
      undefined,
      this.isAxiosError(error) ? error.response?.status : undefined,
      error
    );
  }

  private static isAxiosError (error: unknown): error is AxiosError<ApiErrorResponse> {
    return (
      typeof error === 'object' &&
      error !== null &&
      'isAxiosError' in error &&
      (error as AxiosError).isAxiosError === true
    );
  }

  private static getErrorTypeFromCode (code: number): ErrorType {
    switch (code) {
    case 400:
      return ErrorType.VALIDATION;
    case 401:
      return ErrorType.AUTHENTICATION;
    case 403:
      return ErrorType.AUTHORIZATION;
    case 404:
      return ErrorType.NOT_FOUND;
    case 500:
      return ErrorType.SERVER;
    default:
      if (code >= 400 && code < 500) {
        return ErrorType.VALIDATION;
      }
      if (code >= 500) {
        return ErrorType.SERVER;
      }
      return ErrorType.UNKNOWN;
    }
  }

  toJSON () {
    return {
      name: this.name,
      message: this.message,
      type: this.type,
      details: this.details,
      code: this.code
    };
  }
}
